import React, { useState, useEffect, useRef } from 'react'
import { Icon } from 'antd'
import { T, fmtSec } from './SharedLayout'

/**
 * CombinedPlayer
 *
 * Synchronously plays both narration and dialogue tracks for a scene.
 */
const CombinedPlayer = ({
  narrationSrc,
  dialogueSrc,
  duration,
  playheadMs,
  onPlayheadChange,
  isPlaying: externalPlaying,
  onPlayingChange
}) => {
  const [pct, setPct] = useState(0)
  const [cur, setCur] = useState(0)
  const narRef = useRef(null)
  const diaRef = useRef(null)
  const rafRef = useRef(null)

  useEffect(() => {
    if (narrationSrc) narRef.current = new Audio(narrationSrc)
    if (dialogueSrc) diaRef.current = new Audio(dialogueSrc)
    const nar = narRef.current
    if (nar) {
      nar.addEventListener('ended', () => {
        onPlayingChange && onPlayingChange(false)
        setPct(0)
        setCur(0)
        cancelAnimationFrame(rafRef.current)
      })
    }
    return () => {
      narRef.current && narRef.current.pause()
      if (narRef.current) narRef.current.src = ''
      diaRef.current && diaRef.current.pause()
      if (diaRef.current) diaRef.current.src = ''
      cancelAnimationFrame(rafRef.current)
    }
  }, [narrationSrc, dialogueSrc, onPlayingChange])

  useEffect(() => {
    if (!externalPlaying) {
      narRef.current && narRef.current.pause()
      diaRef.current && diaRef.current.pause()
      cancelAnimationFrame(rafRef.current)
    }
  }, [externalPlaying])

  const tick = () => {
    const a = narRef.current
    if (!a) return
    const d = duration || a.duration || 1
    const t = a.currentTime
    setCur(t)
    setPct((t / d) * 100)
    onPlayheadChange && onPlayheadChange(Math.round(t * 1000))
    rafRef.current = requestAnimationFrame(tick)
  }

  const toggle = () => {
    if (externalPlaying) {
      narRef.current && narRef.current.pause()
      diaRef.current && diaRef.current.pause()
      onPlayingChange && onPlayingChange(false)
      cancelAnimationFrame(rafRef.current)
    } else {
      narRef.current && narRef.current.play().catch(() => {})
      diaRef.current && diaRef.current.play().catch(() => {})
      onPlayingChange && onPlayingChange(true)
      rafRef.current = requestAnimationFrame(tick)
    }
  }

  const seek = e => {
    const d = duration || narRef.current?.duration || 1
    const r = e.currentTarget.getBoundingClientRect()
    const t = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * d
    if (narRef.current) narRef.current.currentTime = t
    if (diaRef.current) diaRef.current.currentTime = t
    setCur(t)
    setPct((t / d) * 100)
    onPlayheadChange && onPlayheadChange(Math.round(t * 1000))
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 14px',
        borderRadius: 8,
        border: `1px solid ${T.border}`,
        background: T.surface,
        marginTop: 8
      }}
    >
      <button
        onClick={toggle}
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          padding: 0,
          flexShrink: 0,
          border: `1.5px solid ${externalPlaying ? T.ink : T.border}`,
          background: externalPlaying ? T.ink : T.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.13s'
        }}
      >
        <Icon
          type={externalPlaying ? 'pause' : 'caret-right'}
          style={{
            fontSize: 13,
            color: externalPlaying ? '#fff' : T.inkSub,
            marginLeft: externalPlaying ? 0 : 2
          }}
        />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            marginBottom: 5
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: T.inkMid,
              textTransform: 'uppercase',
              letterSpacing: '0.4px'
            }}
          >
            Combined
          </span>
          <span style={{ fontSize: 11, color: T.inkLight }}>
            narration + dialogue simultaneously
          </span>
        </div>
        <div
          onClick={seek}
          style={{
            height: 4,
            background: T.borderLight,
            borderRadius: 2,
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: '0 auto 0 0',
              width: `${pct}%`,
              background: T.ink,
              borderRadius: 2,
              transition: 'width 0.05s linear'
            }}
          />
        </div>
      </div>
      <span
        style={{
          fontSize: 11,
          color: T.inkLight,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0
        }}
      >
        {fmtSec(cur)} / {fmtSec(duration || 0)}
      </span>
    </div>
  )
}

export default CombinedPlayer
